import type { PDFDocumentProxy } from 'pdfjs-dist';

// pdf.jsの遅延初期化
let pdfjsLib: typeof import('pdfjs-dist') | null = null;

// PDFドキュメントのキャッシュ（同じファイルの二重読み込みを防ぐ）
interface CachedPDF {
  fileName: string;
  fileSize: number;
  lastModified: number;
  document: PDFDocumentProxy;
}
let cachedPDF: CachedPDF | null = null;

// PDFドキュメントを取得するヘルパー関数
async function loadPdfDocument(arrayBuffer: ArrayBuffer): Promise<PDFDocumentProxy> {
  console.log('[PDF] loadPdfDocument called, arrayBuffer size:', arrayBuffer.byteLength);

  // pdfjs-distをまだ読み込んでいない場合
  if (!pdfjsLib) {
    // 本体をインポート
    pdfjsLib = await import('pdfjs-dist');
    console.log('[PDF] pdfjs loaded, version:', pdfjsLib.version);

    // Worker URLを設定（CDN使用、バージョンを動的に取得）
    const { GlobalWorkerOptions } = pdfjsLib;
    const workerUrl = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
    GlobalWorkerOptions.workerSrc = workerUrl;
    console.log('[PDF] Worker src set to:', workerUrl);
  }

  console.log('[PDF] Creating loading task...');
  const loadingTask = pdfjsLib.getDocument({
    data: arrayBuffer,
  });

  // プログレス監視
  loadingTask.onProgress = (progress: { loaded: number; total: number }) => {
    console.log('[PDF] Loading progress:', Math.round((progress.loaded / progress.total) * 100), '%');
  };

  console.log('[PDF] Waiting for document to load...');

  // タイムアウト付きで読み込み
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('PDF loading timeout after 30s')), 30000);
  });

  try {
    const pdf = await Promise.race([loadingTask.promise, timeoutPromise]);
    console.log('[PDF] Document loaded, numPages:', pdf.numPages);
    return pdf;
  } catch (e) {
    console.error('[PDF] Failed to load document:', e);
    throw e;
  }
}

// キャッシュを使用してPDFドキュメントを取得
async function getOrLoadPdfDocument(file: File): Promise<PDFDocumentProxy> {
  // キャッシュが有効かチェック
  if (
    cachedPDF &&
    cachedPDF.fileName === file.name &&
    cachedPDF.fileSize === file.size &&
    cachedPDF.lastModified === file.lastModified
  ) {
    console.log('[PDF] Using cached document');
    return cachedPDF.document;
  }

  // 新しくロード
  const arrayBuffer = await file.arrayBuffer();
  const document = await loadPdfDocument(arrayBuffer);

  // キャッシュを更新
  cachedPDF = {
    fileName: file.name,
    fileSize: file.size,
    lastModified: file.lastModified,
    document,
  };

  return document;
}

export interface PDFPageInfo {
  pageNumber: number;
  width: number;
  height: number;
  thumbnailDataUrl: string;
}

export interface PDFInfo {
  numPages: number;
  pages: PDFPageInfo[];
}

/**
 * PDFのページ数のみを高速に取得（サムネイルなし）
 */
export async function getPDFPageCount(file: File): Promise<number> {
  const startTime = performance.now();
  const pdf = await getOrLoadPdfDocument(file);
  const endTime = performance.now();
  console.log('[PDF] getPDFPageCount complete in', Math.round(endTime - startTime), 'ms');
  return pdf.numPages;
}

/**
 * 指定ページのサムネイルを取得
 */
export async function getPDFPageThumbnail(file: File, pageNumber: number, thumbnailScale = 0.3): Promise<PDFPageInfo> {
  const pdf = await getOrLoadPdfDocument(file);
  return renderPageThumbnail(pdf, pageNumber, thumbnailScale);
}

/**
 * PDFファイルの情報を取得（ページ数、各ページのサムネイル）
 * パフォーマンス改善: 並列レンダリング + JPEG形式 + キャッシュ + 低解像度サムネイル
 */
export async function getPDFInfo(file: File, thumbnailScale = 0.3): Promise<PDFInfo> {
  const startTime = performance.now();
  console.log('[PDF] getPDFInfo start');

  const pdf = await getOrLoadPdfDocument(file);
  console.log('[PDF] Document ready, rendering', pdf.numPages, 'pages');

  // 全ページを並列でレンダリング
  const pagePromises: Promise<PDFPageInfo>[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    pagePromises.push(renderPageThumbnail(pdf, i, thumbnailScale));
  }

  const pages = await Promise.all(pagePromises);

  const endTime = performance.now();
  console.log('[PDF] getPDFInfo complete in', Math.round(endTime - startTime), 'ms');

  return {
    numPages: pdf.numPages,
    pages,
  };
}

/**
 * 単一ページのサムネイルをレンダリング
 */
async function renderPageThumbnail(
  pdf: PDFDocumentProxy,
  pageNumber: number,
  scale: number
): Promise<PDFPageInfo> {
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale });

  // サムネイル用のキャンバスを作成
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const context = canvas.getContext('2d');

  if (context) {
    await page.render({
      canvasContext: context,
      viewport: viewport,
    }).promise;
  }

  // JPEG形式で出力（PNGより軽量）
  const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);

  return {
    pageNumber,
    width: page.getViewport({ scale: 1 }).width,
    height: page.getViewport({ scale: 1 }).height,
    thumbnailDataUrl,
  };
}

/**
 * PDFの指定ページを画像に変換
 * パフォーマンス改善: キャッシュされたドキュメントを使用
 */
export async function convertPDFPageToImage(
  file: File,
  pageNumber: number,
  scale = 2.0 // 高解像度のため2倍
): Promise<{ dataUrl: string; blob: Blob; width: number; height: number }> {
  const startTime = performance.now();
  console.log('[PDF] convertPDFPageToImage start, page:', pageNumber);

  const pdf = await getOrLoadPdfDocument(file);

  if (pageNumber < 1 || pageNumber > pdf.numPages) {
    throw new Error(`Invalid page number: ${pageNumber}. PDF has ${pdf.numPages} pages.`);
  }

  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Failed to get canvas context');
  }

  await page.render({
    canvasContext: context,
    viewport: viewport,
  }).promise;

  const dataUrl = canvas.toDataURL('image/png');

  // BlobをPromiseで取得
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) {
        resolve(b);
      } else {
        reject(new Error('Failed to convert canvas to blob'));
      }
    }, 'image/png');
  });

  const endTime = performance.now();
  console.log('[PDF] convertPDFPageToImage complete in', Math.round(endTime - startTime), 'ms');

  return {
    dataUrl,
    blob,
    width: viewport.width,
    height: viewport.height,
  };
}

/**
 * ファイルがPDFかどうかを判定
 */
export function isPDFFile(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

/**
 * PDFキャッシュをクリア
 */
export function clearPDFCache(): void {
  cachedPDF = null;
  console.log('[PDF] Cache cleared');
}
